import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { MemberNameLink } from '@/components/shared/member-name-link';
import { formatDate, formatDateTime } from '@/utils/format';

const formatOccurredOn = (value: string, locale: string) =>
  value.includes('T')
    ? formatDateTime(value, { locale })
    : formatDate(value, { locale, style: 'medium' });

import {
  type DashboardContentPreview,
  type DashboardFollowUpPreview,
  type DashboardJoinRequestPreview,
  type DashboardPersonPreview,
} from './dashboard-content';

const personName = (person: { firstName: string; lastName: string }) =>
  [person.firstName, person.lastName].filter(Boolean).join(' ');

export const DashboardPersonPreviewList = ({
  people,
  locale,
  showDate = false,
  showPhoto = true,
}: {
  people: DashboardPersonPreview[];
  locale: string;
  showDate?: boolean;
  showPhoto?: boolean;
}) => (
  <ul className="space-y-1.5">
    {people.map((person) => (
      <li key={person.id} className="flex items-center justify-between gap-3 text-body-sm">
        <MemberNameLink
          memberId={person.id}
          name={personName(person)}
          photoUrl={person.photoUrl}
          showPhoto={showPhoto}
          className="min-w-0 font-medium"
        />
        {showDate && person.joinedAt ? (
          <span className="shrink-0 text-caption text-fg-tertiary tabular-nums">
            {formatDate(person.joinedAt, { locale, style: 'medium' })}
          </span>
        ) : null}
      </li>
    ))}
  </ul>
);

export const DashboardFollowUpPreviewList = ({
  items,
  locale,
}: {
  items: DashboardFollowUpPreview[];
  locale: string;
}) => {
  const { t } = useTranslation('people');

  return (
    <ul className="space-y-1.5">
      {items.map((item) => (
        <li key={item.id} className="flex items-center justify-between gap-3 text-body-sm">
          <MemberNameLink
            memberId={item.memberId}
            name={personName(item)}
            className="min-w-0 font-medium"
          />
          <span className="shrink-0 text-caption text-fg-tertiary">
            {item.dueDate
              ? formatDate(item.dueDate, { locale, style: 'medium' })
              : t(`followUps.reason.${item.reason}`, { defaultValue: item.reason })}
          </span>
        </li>
      ))}
    </ul>
  );
};

export const DashboardJoinRequestPreviewList = ({
  items,
}: {
  items: DashboardJoinRequestPreview[];
}) => (
  <ul className="space-y-1.5">
    {items.map((item) => (
      <li key={item.id} className="truncate text-body-sm font-medium text-fg">
        {personName(item)}
      </li>
    ))}
  </ul>
);

export const DashboardContentPreviewList = ({
  items,
  locale,
  to,
}: {
  items: DashboardContentPreview[];
  locale: string;
  to: string;
}) => (
  <ul className="space-y-1.5">
    {items.map((item) => (
      <li key={item.id}>
        <Link
          to={to}
          className="flex items-center justify-between gap-3 rounded-sm text-body-sm hover:text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
        >
          <span className="min-w-0 truncate font-medium">{item.title}</span>
          {item.occurredOn ? (
            <span className="shrink-0 text-caption text-fg-tertiary tabular-nums">
              {formatOccurredOn(item.occurredOn, locale)}
            </span>
          ) : null}
        </Link>
      </li>
    ))}
  </ul>
);
